import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { api } from '@/api/client';
import type { components } from '@/api/schema';
import { AppIcon } from '@/components/AppIcon';
import { PackedQtyInput } from '@/components/PackedQtyInput';
import {
  PackingEditor,
  packagingsForEntry,
  packagingsToSend,
  packingProblem,
  packRowsFrom,
  type CountingUnit,
  type PackingValue,
} from '@/components/PackingEditor';
import { SearchSelect } from '@/components/SearchSelect';
import { ErrorNote, Field, FormActions, FormGroup, FormPanel, OkNote, errorMessage } from '@/components/ui';
import { ALL_SKUS_KEY, useCategories, useCodePreview, useUoms } from '@/hooks/inventory';
import { awaitWork, retryWork, saveWork } from '@/offline/engine';
import { localId, newOperationId, type NewOperation, type Operation } from '@/offline/outbox';
import { NO_CODE_YET, pendingSku, usePendingRows, type PendingSkuForm } from '@/offline/pending';
import { useConnection } from '@/stores/connection';
import { announceSavedOffline } from '@/stores/savedOffline';
import { createOp, saveReference, type ReferenceSaved } from './referenceOffline';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useAuth } from '@/stores/auth';
import { packagingsOf, unitFamily, type PackingUnit } from '@/packing';

type SkuRow = components['schemas']['SkuListItemDto'];

/**
 * Stands in for the id of a category or unit typed into its picker that does
 * not exist yet. It is created only when the item is saved, so a form that is
 * abandoned halfway leaves no stray category or unit behind.
 */
const PENDING_ID = 'pending';

/**
 * How a unit typed in here is counted until someone reviews it in Units. Two
 * decimals, so a quantity like 1.5 is never shown rounded in the meantime.
 */
const PROVISIONAL_PRECISION = 2;

/** The server's rule for a taken SKU, as near as this PC can apply it: "00008" is SKU-00008. */
function sameSkuCode(code: string, typed: string): boolean {
  const digits = (c: string) => c.replace(/\D/g, '').replace(/^0+/, '');
  if (code.toLowerCase() === typed.toLowerCase()) return true;
  return /^(sku-?)?\d+$/i.test(code) && digits(typed) !== '' && digits(code) === digits(typed);
}

/** Names compared as the server compares them: case, spacing and punctuation aside. */
function sameName(a: string, b: string): boolean {
  const plain = (n: string) => n.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
  return plain(a) === plain(b);
}

export function SkuForm({
  initial,
  fixing,
  onCancel,
  onSaved,
}: {
  initial?: SkuRow | undefined;
  /** An item added on this PC that the server refused, being put right. */
  fixing?: Operation | undefined;
  onCancel: () => void;
  onSaved: (message: string) => void;
}) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const online = useConnection((s) => s.online);
  const editing = initial !== undefined;
  const preview = useCodePreview('Sku', !editing && online);
  const formRef = useRef<HTMLFormElement>(null);
  const pendingSkus = usePendingRows('sku.create');
  // Refused work comes back as it was typed.
  const typedBefore = fixing ? pendingSku(fixing) : undefined;
  const seed = initial ?? typedBefore;

  const [form, setForm] = useState(() => ({
    code: typedBefore && typedBefore.code !== NO_CODE_YET ? typedBefore.code : '',
    nameEn: seed?.nameEn ?? '',
    nameUr: seed?.nameUr ?? '',
    categoryId: seed?.categoryId ?? '',
    categoryLabel: seed?.category ?? '',
    uomId: seed?.uomId ?? '',
    uomLabel: typedBefore?.uom ?? '',
    packing: {
      contentQty: seed?.contentQty ?? '',
      contentUomId: seed?.contentUomId ?? '',
      contentUomLabel: seed?.contentUom ?? '',
      packagings: packRowsFrom(seed?.packagings),
    } as PackingValue,
    reorderLevel: seed?.reorderLevel ?? '',
    expiryWarnDays: seed?.expiryWarnDays?.toString() ?? '',
    isBatchTracked: seed?.isBatchTracked ?? false,
    hasExpiry: seed?.hasExpiry ?? false,
  }));
  const [problem, setProblem] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Ask the server whether the typed SKU is taken, once typing pauses. It
  // applies the same rule as on save: "00008" is taken when SKU-00008 exists,
  // deleted items included.
  const typedCode = form.code.trim();
  const checkedCode = useDebouncedValue(typedCode);
  const codeCheck = useQuery({
    queryKey: ['inv', 'skus', 'code-check', checkedCode.toLowerCase()],
    queryFn: () => api.get('/api/v1/inventory/skus/code-check', { code: checkedCode }),
    enabled: !editing && checkedCode !== '',
    staleTime: 0,
  });
  // With no line, checked against the items on this PC and those waiting to be
  // sent. The server checks again when it is sent, and a clash comes back to
  // Needs attention to be put right.
  const onPc = useMemo(
    () => [
      ...(queryClient.getQueryData<SkuRow[]>(ALL_SKUS_KEY) ?? []),
      ...pendingSkus.filter((r) => !fixing || r.id !== localId(fixing.id)),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pendingSkus, fixing, online],
  );
  const offlineCodeClash = !online && typedCode ? onPc.find((r) => sameSkuCode(r.code, typedCode)) : undefined;
  const codeSettled = online ? checkedCode === typedCode && codeCheck.isSuccess : true;
  const existing = online ? (codeSettled ? codeCheck.data?.clash : undefined) : offlineCodeClash;

  // The same for the name, which no two items may share either. The server
  // ignores case, spacing and punctuation, and counts retired items; the item
  // being edited may keep its own name.
  const typedName = form.nameEn.trim();
  const checkedName = useDebouncedValue(typedName);
  const nameCheck = useQuery({
    queryKey: ['inv', 'skus', 'name-check', checkedName.toLowerCase(), initial?.id ?? null],
    queryFn: () =>
      api.get('/api/v1/inventory/skus/name-check', {
        name: checkedName,
        ...(initial ? { exceptId: initial.id } : {}),
      }),
    enabled: checkedName !== '',
    staleTime: 0,
  });
  const nameTaken = online
    ? checkedName === typedName && nameCheck.isSuccess
      ? nameCheck.data.clash
      : undefined
    : typedName
      ? onPc.find((r) => sameName(r.nameEn, typedName) && r.id !== initial?.id)
      : undefined;

  // With what is waiting on this PC, so a category or unit added offline can be chosen.
  const categories = useCategories();
  const uoms = useUoms();

  /**
   * Creates the category and unit typed in as new, just before the item that
   * uses them. Each id is written back to the form as soon as it exists, so a
   * retry after a failed item save reuses it instead of adding a duplicate.
   * Both are marked for review, since only their name was given here.
   */
  async function createPending() {
    let { categoryId, uomId } = form;
    if (categoryId === PENDING_ID) {
      const created = await api.post<{ id: string }>('/api/v1/inventory/categories', {
        nameEn: form.categoryLabel,
        needsReview: true,
      });
      categoryId = created.id;
      setForm((f) => ({ ...f, categoryId: created.id }));
      void queryClient.invalidateQueries({ queryKey: ['inv', 'categories'] });
    }
    if (uomId === PENDING_ID) {
      const created = await api.post<{ id: string }>('/api/v1/inventory/uoms', {
        nameEn: form.uomLabel,
        precision: PROVISIONAL_PRECISION,
        needsReview: true,
      });
      uomId = created.id;
      setForm((f) => ({ ...f, uomId: created.id }));
      void queryClient.invalidateQueries({ queryKey: ['inv', 'uoms'] });
    }
    return { categoryId, uomId };
  }

  const save = useMutation({
    mutationFn: async (): Promise<ReferenceSaved> => {
      const packagings = packagingsToSend(form.packing.packagings);
      const contentQty = form.packing.contentQty.trim();
      const optional = {
        ...(form.nameUr.trim() ? { nameUr: form.nameUr.trim() } : {}),
        ...(contentQty ? { contentQty, contentUomId: form.packing.contentUomId } : {}),
        ...(packagings.length ? { packagings } : {}),
        ...(form.reorderLevel ? { reorderLevel: form.reorderLevel } : {}),
        ...(form.expiryWarnDays ? { expiryWarnDays: Number(form.expiryWarnDays) } : {}),
      };
      if (initial) {
        // A change decides against what the server holds now: online only.
        // Every field is sent, a blank one as null, so a cleared box clears.
        const { categoryId, uomId } = await createPending();
        await api.patch(`/api/v1/inventory/skus/${initial.id}`, {
          nameEn: form.nameEn.trim(),
          nameUr: form.nameUr.trim() || null,
          categoryId,
          ...(uomId !== initial.uomId ? { uomId } : {}),
          contentQty: contentQty || null,
          contentUomId: contentQty ? form.packing.contentUomId : null,
          packagings,
          reorderLevel: form.reorderLevel || null,
          expiryWarnDays: form.expiryWarnDays ? Number(form.expiryWarnDays) : null,
        });
        return { code: initial.code, waiting: false };
      }

      // A new item, with the category and unit typed in as new, all through the
      // outbox as one batch (.docs/023): made now with a line, kept on this PC
      // without. Each new one stands in under a local id until it is made, and
      // the item is sent with the server's ids in their place.
      const batch: NewOperation[] = [];
      let { categoryId, uomId } = form;
      if (categoryId === PENDING_ID) {
        const id = newOperationId();
        batch.push({
          id,
          ...createOp('category.create', '/api/v1/inventory/categories', { nameEn: form.categoryLabel, needsReview: true }, `New category: ${form.categoryLabel}`),
        });
        categoryId = localId(id);
      }
      if (uomId === PENDING_ID) {
        const id = newOperationId();
        batch.push({
          id,
          ...createOp('uom.create', '/api/v1/inventory/uoms', { nameEn: form.uomLabel, precision: PROVISIONAL_PRECISION, needsReview: true }, `New unit: ${form.uomLabel}`),
        });
        uomId = localId(id);
      }
      const name = form.nameEn.trim();
      const body = {
        // The server checks the SKU again inside the insert's transaction, so
        // two people typing the same one at once cannot both save it.
        code: typedCode,
        nameEn: name,
        categoryId,
        uomId,
        isBatchTracked: form.isBatchTracked,
        hasExpiry: form.isBatchTracked && form.hasExpiry,
        ...optional,
      };
      const unit = uoms.data?.find((u) => u.id === form.uomId);
      const holds = uoms.data?.find((u) => u.id === form.packing.contentUomId);
      const shown: PendingSkuForm = {
        categoryLabel: form.categoryLabel || (categories.data?.find((c) => c.id === form.categoryId)?.nameEn ?? ''),
        uomLabel: unit?.abbreviation || unit?.nameEn || form.uomLabel,
        uomPrecision: unit?.precision ?? PROVISIONAL_PRECISION,
        ...(holds ? { contentUomLabel: holds.code, contentUomPrecision: holds.precision } : {}),
      };
      const describes = `New item: ${typedCode ? `${typedCode} ` : ''}${name}`;
      if (fixing) {
        // A new category or unit goes first; the item after it, so it can wait for them.
        if (batch.length) await saveWork(batch);
        const newId = await retryWork(fixing.id, { body, form: shown, describes, toEnd: batch.length > 0 });
        const saved = newId ? await awaitWork([newId]) : null;
        if (!saved || saved.outcome === 'refused' || saved.outcome === 'not-kept') {
          throw new Error(saved?.ops[0]?.lastError ?? 'The server still refused it. It is kept under Offline work, Needs attention.');
        }
        if (saved.outcome === 'waiting') {
          void announceSavedOffline({ what: `Item “${name}”`, closeLabel: 'OK' });
          return { code: '', waiting: true };
        }
        return { code: (saved.ops[0]?.result as { code?: string } | null)?.code ?? typedCode, waiting: false };
      }
      batch.push(createOp('sku.create', '/api/v1/inventory/skus', body, describes, shown));
      return saveReference(batch, name);
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['inv', 'categories'] });
      void queryClient.invalidateQueries({ queryKey: ['inv', 'uoms'] });
      if (editing) {
        onSaved(`${result.code} updated.`);
        return;
      }
      const said = result.waiting
        ? `“${form.nameEn.trim()}” is saved on this PC. It is made when the connection is back, and can be used in Stock IN now.`
        : `${result.code} added to the item list.`;
      if (fixing) {
        onSaved(said);
        return;
      }
      onSaved(said);
      setMessage(result.waiting ? `${said} Add another item, or close the form.` : `${result.code} added. Add another item, or close the form.`);
      setForm((f) => ({
        ...f,
        code: '',
        nameEn: '',
        nameUr: '',
        packing: { contentQty: '', contentUomId: '', contentUomLabel: '', packagings: [] },
        reorderLevel: '',
        // A category or unit typed as new is now waiting, or made: chosen again it is a new one.
        ...(f.categoryId === PENDING_ID ? { categoryId: '', categoryLabel: '' } : {}),
        ...(f.uomId === PENDING_ID ? { uomId: '', uomLabel: '' } : {}),
      }));
      if (online) void preview.refetch();
    },
  });

  function submit() {
    setMessage(null);
    if (formRef.current && !formRef.current.reportValidity()) return;
    const issue = !editing && existing
      ? `SKU ${existing.code} already exists (${existing.nameEn}). Use a different SKU.`
      : nameTaken
      ? nameTaken.isActive
        ? `${nameTaken.code} is already named “${nameTaken.nameEn}”. Use a different name.`
        : `${nameTaken.code} “${nameTaken.nameEn}” is retired under this name. Bring it back instead, or use a different name.`
      : !form.categoryId
      ? 'Choose a category, or type a new name to add one.'
      : !form.uomId
        ? 'Choose a unit.'
        : packingProblem(form.packing, packingContext);
    setProblem(issue);
    if (!issue) save.mutate();
  }

  const categoryOptions = (categories.data ?? []).map((c) => ({ value: c.id, label: c.nameEn, meta: c.code }));
  const unitOptions = (uoms.data ?? []).map((u) => ({ value: u.id, label: u.nameEn, meta: u.code }));
  const canAddCategory = can('inv.category.create');
  const canAddUnit = can('inv.uom.create');
  const newCategory = form.categoryId === PENDING_ID;
  const newUnit = form.uomId === PENDING_ID;

  // The unit stock is counted in, which every package size is measured in.
  const asPackingUnit = (u: { code: string; nameEn: string; abbreviation?: string | null; precision: number }): PackingUnit => ({
    label: u.abbreviation || u.code,
    names: [u.code, u.nameEn, u.abbreviation ?? ''],
    precision: u.precision,
  });
  const chosenUnit = uoms.data?.find((u) => u.id === form.uomId);
  const countingUnit: CountingUnit | null = chosenUnit
    ? { ...asPackingUnit(chosenUnit), family: unitFamily(chosenUnit.code, chosenUnit.nameEn, chosenUnit.abbreviation) }
    : newUnit && form.uomLabel
      ? { label: form.uomLabel, names: [form.uomLabel], precision: PROVISIONAL_PRECISION, family: unitFamily(form.uomLabel) }
      : null;
  const contentUnitFor = (id: string) => {
    const u = uoms.data?.find((x) => x.id === id);
    return u ? asPackingUnit(u) : undefined;
  };
  const packingContext = countingUnit
    ? { unit: countingUnit, contentUnit: form.packing.contentUomId ? contentUnitFor(form.packing.contentUomId) : undefined }
    : null;
  // Weights and volumes first: what a bag or a bottle usually holds.
  const contentOptions = (uoms.data ?? [])
    .filter((u) => u.id !== form.uomId)
    .map((u) => ({ value: u.id, label: u.nameEn, meta: u.code, family: unitFamily(u.code, u.nameEn, u.abbreviation) }))
    .sort((a, b) => Number(b.family === 'weight' || b.family === 'volume') - Number(a.family === 'weight' || a.family === 'volume'))
    .map(({ family: _family, ...o }) => o);
  // Package names already used on other items, so "Carton" is spelt one way.
  const packageNames = useMemo(() => {
    const all = queryClient.getQueryData<SkuRow[]>(ALL_SKUS_KEY) ?? [];
    return [...new Set(all.flatMap((r) => packagingsOf(r).flatMap((p) => [p.name, p.innerName ?? ''])).filter(Boolean))].sort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online]);
  // The packing as a quantity box reads it, for typing the reorder level in packages.
  const asItem = countingUnit
    ? {
        uom: countingUnit.label,
        uomPrecision: countingUnit.precision,
        contentQty: form.packing.contentQty || null,
        contentUom: packingContext?.contentUnit?.label ?? null,
        contentUomPrecision: form.packing.contentUomId ? (uoms.data?.find((u) => u.id === form.packing.contentUomId)?.precision ?? null) : null,
        packagings: packagingsForEntry(form.packing.packagings, packingContext),
      }
    : null;
  const unitChanged = editing && form.uomId !== initial.uomId && form.packing.packagings.length > 0;

  return (
    <FormPanel
      icon={<AppIcon kind="item" size={18} />}
      title={editing ? `Edit ${initial.code}` : fixing ? 'Put right a refused item' : 'Add an item'}
      intro="The code is printed on every document for this item. It cannot be changed after you save."
    >
      <form
        ref={formRef}
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {/* A new SKU is at most 6 digits, so its box is narrow; an existing
            code may be a longer imported one, so editing keeps equal columns. */}
        <FormGroup title="Identity" {...(editing ? { columns: 3 as const } : { template: 'minmax(9rem, 0.7fr) 2fr 2fr' })} card>
          {editing && (
            <Field
              label="Code"
              mono
              value={initial.code}
              onChange={() => {}}
              disabled
              hint="Given when it was created, and never changes"
            />
          )}
          {!editing && (
            <Field
              label="SKU"
              required
              mono
              numeric
              maxLength={6}
              value={form.code}
              onChange={(code) => setForm((f) => ({ ...f, code: code.replace(/\D/g, '').slice(0, 6) }))}
              placeholder={preview.data ? `e.g. ${preview.data.code.replace(/\D/g, '').slice(-6)}` : ''}
              invalid={!!existing}
              hint={
                typedCode === '' ? (
                  'Up to 6 digits. Fixed once saved.'
                ) : existing ? (
                  <TakenHint item={existing} />
                ) : !online ? (
                  'Not used by any item on this PC. The server checks again when it is sent.'
                ) : codeSettled ? (
                  'Available'
                ) : (
                  'Checking…'
                )
              }
            />
          )}
          <Field
            label="Name"
            required
            value={form.nameEn}
            onChange={(nameEn) => setForm((f) => ({ ...f, nameEn }))}
            invalid={!!nameTaken}
            hint={nameTaken ? <TakenHint item={nameTaken} /> : 'e.g. Layer Mash 50 kg'}
          />
          <Field label="Name (Urdu)" urdu value={form.nameUr} onChange={(nameUr) => setForm((f) => ({ ...f, nameUr }))} />
        </FormGroup>

        <FormGroup title="Classification" columns={2} card>
          <SearchSelect
            label="Category"
            required
            value={form.categoryId}
            selectedLabel={form.categoryLabel || undefined}
            options={categoryOptions}
            placeholder="Search categories…"
            onChange={(option) => setForm((f) => ({ ...f, categoryId: option?.value ?? '', categoryLabel: option?.label ?? '' }))}
            {...(canAddCategory
              ? {
                  onCreate: (name: string) => setForm((f) => ({ ...f, categoryId: PENDING_ID, categoryLabel: name })),
                  createLabel: (name: string) => `+ Add “${name}” as a new category`,
                }
              : {})}
            hint={
              newCategory
                ? <NewEntryHint kind="category" />
                : canAddCategory
                  ? 'Type a new name to add a category'
                  : undefined
            }
          />
          <SearchSelect
            label="Unit of measure"
            required
            value={form.uomId}
            selectedLabel={form.uomLabel || undefined}
            options={unitOptions}
            placeholder="Search units…"
            onChange={(option) => setForm((f) => ({ ...f, uomId: option?.value ?? '', uomLabel: option?.label ?? '' }))}
            {...(canAddUnit
              ? {
                  onCreate: (name: string) => setForm((f) => ({ ...f, uomId: PENDING_ID, uomLabel: name })),
                  createLabel: (name: string) => `+ Add “${name}” as a new unit`,
                }
              : {})}
            hint={
              newUnit
                ? <NewEntryHint kind="unit" />
                : editing
                  ? 'You can change this only before any stock moves'
                  : canAddUnit
                    ? 'Type a new name to add a unit'
                    : 'How this item is counted'
            }
          />
        </FormGroup>

        <FormGroup
          title="Packing"
          card
          hint={
            countingUnit
              ? `Stock is always counted in ${countingUnit.label}. Packages only change how quantities are typed and shown, so a size can be corrected later without changing any stock.`
              : undefined
          }
        >
          <PackingEditor
            value={form.packing}
            onChange={(packing) => setForm((f) => ({ ...f, packing }))}
            unit={countingUnit}
            contentUnits={contentOptions}
            contentUnitFor={contentUnitFor}
            nameSuggestions={packageNames}
          />
          {unitChanged && (
            <p className="packing__problem">
              The unit changed from {initial.uom} to {countingUnit?.label}: check each package size is now in {countingUnit?.label}.
            </p>
          )}
        </FormGroup>

        <FormGroup
          title="Tracking & alerts"
          card
          hint={editing ? 'Lot and expiry tracking cannot change later, because stock history depends on them.' : undefined}
        >
          <div className="formgrid formgrid--3">
            <PackedQtyInput
              label="Reorder level"
              item={asItem}
              itemKey={form.uomId}
              value={form.reorderLevel}
              onChange={(reorderLevel) => setForm((f) => ({ ...f, reorderLevel }))}
              hint="Marked low stock below this. Leave blank for no alert."
            />
          </div>
          <label className="settings__toggle">
            <input
              type="checkbox"
              checked={form.isBatchTracked}
              disabled={editing}
              onChange={(e) =>
                setForm((f) => ({ ...f, isBatchTracked: e.target.checked, hasExpiry: e.target.checked && f.hasExpiry }))
              }
            />
            <span>
              <strong>Track by lot</strong>
              <span className="dim"> — each receipt and issue records its lot (vaccines, medicines)</span>
            </span>
          </label>
          <label className="settings__toggle">
            <input
              type="checkbox"
              checked={form.hasExpiry}
              disabled={editing || !form.isBatchTracked}
              onChange={(e) => setForm((f) => ({ ...f, hasExpiry: e.target.checked }))}
            />
            <span>
              <strong>Track expiry</strong>
              <span className="dim">
                {form.isBatchTracked ? ' — each lot keeps its expiry date' : ' — needs lot tracking, because the date belongs to a lot'}
              </span>
            </span>
          </label>
          {form.hasExpiry && (
            <div className="formgrid formgrid--3 packing__indent">
              <Field
                label="Expiry warning (days)"
                type="number"
                min={1}
                max={3650}
                value={form.expiryWarnDays}
                onChange={(expiryWarnDays) => setForm((f) => ({ ...f, expiryWarnDays }))}
                hint="Warn this many days before a lot expires. 60 if blank."
              />
            </div>
          )}
        </FormGroup>

        {fixing?.lastError && !problem && !save.isError && <ErrorNote message={fixing.lastError} />}
        {problem && <ErrorNote message={problem} />}
        {save.isError && <ErrorNote message={errorMessage(save.error, 'Could not save')} />}
        {message && <OkNote message={message} />}

        <FormActions onCancel={onCancel} cancelLabel={editing ? 'Cancel' : 'Back to list'}>
          <button type="submit" className="btn btn--primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Add item'}
          </button>
        </FormActions>
      </form>
    </FormPanel>
  );
}

/** Names the item already using a typed SKU or name, so the user can tell whether it is the one they meant. */
function TakenHint({ item }: { item: { code: string; nameEn: string; isActive?: boolean } }) {
  return (
    <span style={{ color: 'var(--c-alert)' }}>
      Already used by {item.code} — {item.nameEn}
      {item.isActive === false && ' (retired)'}
    </span>
  );
}

/** Tells the user a typed category or unit is not saved yet, and where to finish it. */
function NewEntryHint({ kind }: { kind: 'category' | 'unit' }) {
  return (
    <span>
      <span className="pill">New</span> Added when you save the item.{' '}
      {kind === 'category'
        ? 'You can finish its details later in Categories.'
        : 'Choose how it is counted later in Units.'}
    </span>
  );
}

/** Placeholder for screens that are mapped but not yet built. */
export function NotBuiltYet({ label }: { label: string }) {
  return (
    <div className="panel panel--empty">
      <h2>{label}</h2>
      <p className="muted">
        This screen is in the module map but has not been built yet. The data model behind it
        already exists, so nothing here needs redesigning when it is.
      </p>
    </div>
  );
}
